import '@fontsource-variable/plus-jakarta-sans';
import '@fontsource/jetbrains-mono/400.css';
import './styles.css';
import { Spinner } from '@blink/ui';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { startOutbox } from '@/lib/outbox';
import { ShiftTracker } from '@/components/shift';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { I18nProvider } from '@/i18n';
import { ApiClientError, api, type AuthMe } from '@/lib/api';
import { ChangePassword } from '@/pages/ChangePassword';
import { Home } from '@/pages/Home';
import { Login } from '@/pages/Login';
import { Load } from '@/pages/Load';
import { Parcel } from '@/pages/Parcel';
import { Deliver } from '@/pages/Deliver';
import { Fail } from '@/pages/Fail';
import { MyCash } from '@/pages/MyCash';
import { Help, HelpArticlePage } from '@/pages/Help';

const client = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // weak signal: try again for about 15 s (1 + 2 + 4 + 8 s) before showing an error
      retry: (count, err) => !(err instanceof ApiClientError && err.status > 0 && err.status < 500) && count < 4,
    },
    // actions always run, even when the phone says it is offline: the outbox (lib/outbox.ts)
    // keeps door scans, photos, Delivered and Not delivered on the phone until the signal is back
    mutations: { networkMode: 'always' },
  },
});

/** Signed-in screens: sends the courier to sign in, or to choose a password first. */
function SignedIn({ children }: { children: ReactNode }) {
  const location = useLocation();
  const me = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api<AuthMe>('/auth/me'), retry: false });
  if (me.isLoading) return <Spinner className="mx-auto mt-24" />;
  if (!me.data) return <Navigate to="/login" replace />;
  if (me.data.user.mustChangePassword && location.pathname !== '/change-password') return <Navigate to="/change-password" replace />;
  return (
    <I18nProvider language={me.data.user.language}>
      {children}
      {/* location only while a shift is open (P3-11) */}
      {me.data.user.mustChangePassword ? null : <ShiftTracker />}
    </I18nProvider>
  );
}

// weak signal: send what is kept on the phone whenever the connection allows
startOutbox();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={client}>
      <I18nProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/change-password" element={<SignedIn><ChangePassword /></SignedIn>} />
            <Route path="/" element={<SignedIn><Home /></SignedIn>} />
            <Route path="/load" element={<SignedIn><Load /></SignedIn>} />
            <Route path="/parcels/:id" element={<SignedIn><Parcel /></SignedIn>} />
            <Route path="/parcels/:id/deliver" element={<SignedIn><Deliver /></SignedIn>} />
            <Route path="/parcels/:id/fail" element={<SignedIn><Fail /></SignedIn>} />
            <Route path="/cash" element={<SignedIn><MyCash /></SignedIn>} />
            <Route path="/help" element={<SignedIn><Help /></SignedIn>} />
            <Route path="/help/:slug" element={<SignedIn><HelpArticlePage /></SignedIn>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </I18nProvider>
    </QueryClientProvider>
  </StrictMode>,
);
